import { AuthenticationService } from '@/services/AuthenticationService.ts';
import { useAuthStore } from '@/stores/AuthStore.ts';
import { createTextToast } from '@/utils/ToastUtils.ts';
import { logOutAction } from '@/actions/auth/LogOutAction.ts';
import { apiService } from '@/services/ApiService.ts';
import { useSessionStore } from '@/stores/SessionStore.ts';
import { useUserInfoStore } from '@/stores/UserInfoStore.ts';
import type { SubscriptionTier } from '@/types/SubscriptionTier.ts';
import { useSettingsStore } from '@/stores/SettingsStore.ts';
import { wsAuthLogIn } from '@/ws/requests/WsAuthLogIn.ts';

const MAX_RETRIES = 3;

/**
 * @throws {Error} On API error response
 */
export const refreshTokenAction = async (retryAttempt: number = 0) => {
    const authStore = useAuthStore();
    if (!authStore.isLoggedIn) {
        return;
    }

    const curRefreshToken = AuthenticationService.instance.getRefreshToken();
    const curFingerprintHash = AuthenticationService.instance.getFingerprintHash();

    if (!curRefreshToken || !curFingerprintHash) {
        createTextToast('Session expired', 'Please log in again', 'destructive', true);

        void logOutAction();

        return;
    }

    const sessionStore = useSessionStore();

    let apiResponse;
    try {
        apiResponse = await apiService.getApi().authRefreshTokenV1({
            refreshTokenRequestDto: {
                refreshToken: curRefreshToken,
                fingerprintHash: curFingerprintHash,
            },
        });
    } catch {
        if (retryAttempt > MAX_RETRIES) {
            createTextToast(
                'Failed to refresh session',
                'Please log in again',
                'destructive',
                !sessionStore.isInsideWebView,
            );

            void logOutAction();
        } else {
            void refreshTokenAction(retryAttempt + 1);
        }

        return;
    }

    const { refreshToken, accessTokenExpiresIn, fingerprintHash, accessToken } =
        apiResponse.tokenData;

    AuthenticationService.instance.storeTokenData(
        accessToken,
        refreshToken,
        fingerprintHash,
        accessTokenExpiresIn,
    );

    authStore.isLoggedIn = true;

    const { creditsRemaining, username: userUsername, subscriptionTier } = apiResponse.userData;

    const userInfoStore = useUserInfoStore();
    userInfoStore.username = userUsername;
    userInfoStore.creditsRemaining = creditsRemaining;
    userInfoStore.subscriptionTier = subscriptionTier as SubscriptionTier;

    const settingsStore = useSettingsStore();
    settingsStore.rememberedUsername = userUsername;

    wsAuthLogIn(accessToken, !sessionStore.isInsideWebView);
};
